// One list of recommendations for what the analysis views look at, whichever view found them. The workforce report
// finds roles short of capacity, the systems report finds work to integrate, digitise or automate, and the EPM
// scorecard finds where value is created and destroyed; here they are put in one shape, ranked on one scale, and
// merged where they say the same thing (a role overloaded in two reports is one recommendation with two pieces of
// evidence). Pure, so it can be tested without a page
import { EpmDecision, EpmScorecard } from '../epm/epm-scorecard-model';
import { LensKey } from '../lenses/lens';
import { SystemsReport } from '../systems/systems-model';
import { WorkforceReport } from '../workforce/workforce-model';

// what kind of change the recommendation asks for
// service: making a step external roles (customers, suppliers) do easier for them; better service, not staff time saved
export type RecommendationLever = 'capacity' | 'automate' | 'integrate' | 'digitise' | 'eliminate' | 'protect' | 'value' | 'service' | 'data';

export const LEVER_LABELS: Record<RecommendationLever, string> = {
    capacity: 'Add capacity', automate: 'Automate', integrate: 'Integrate', digitise: 'Digitise', eliminate: 'Eliminate',
    protect: 'Protect', value: 'Value', service: 'Customer service', data: 'Fix the data',
};

export type RecommendationSource = 'workforce' | 'systems' | 'epm';

export const SOURCE_LABELS: Record<RecommendationSource, string> = { workforce: 'Workforce report', systems: 'Systems report', epm: 'EPM scorecard' };

export interface Recommendation {
    key: string;
    lever: RecommendationLever;
    priority: number;                   // 1 to 100: to act on now 70 to 100 by how serious, the rest 1 to 69 by their size
    why: string;                        // what the priority rests on, in a sentence
    urgent: boolean;                    // act now, rather than look at
    title: string;
    detail: string;                     // what to do, in a sentence or two
    evidence: string[];                 // the figures behind it, one line from each source
    sources: RecommendationSource[];
    hours: number;                      // people's time a year it concerns
    cost: number;                       // money a year it concerns, about
    models: string[];                   // names
    subjects: string[];                 // the names of the roles, systems, steps or outputs it is about
    lens: LensKey;                      // the view that shows it best
    focus?: string;                     // a node key the view can show
    effort?: 'Low' | 'Medium' | 'High';
}

export interface RecommendationsInput {
    workforce?: WorkforceReport;
    systems?: SystemsReport;
    epm?: EpmScorecard;
}

// how much a recommendation matters: its share of the people's time (or money) in scope, by the lever's weight
const LEVER_WEIGHT: Record<RecommendationLever, number> = { capacity: 1, automate: 0.9, integrate: 0.9, eliminate: 1, protect: 0.8, value: 0.7, digitise: 0.6, service: 0.4, data: 0.3 };
// what isn't a change that saves staff time or money ranks below those that are, however large
const NOT_SAVINGS: RecommendationLever[] = ['data', 'service'];

const EPM_LEVERS: Record<EpmDecision['lever'], RecommendationLever> = {
    eliminate: 'eliminate', automate: 'automate', substitute: 'automate', amplify: 'value', resequence: 'eliminate', protect: 'protect', leverage: 'value', investigate: 'data',
};

const norm = (name: string) => name.trim().toLowerCase();

const titleKey = (title: string) => {
    // the name in quotes, or after "on", "for" or "the", so the same subject found by two sources matches
    const quoted = /"([^"]+)"/.exec(title);
    if (quoted) return norm(quoted[1]);
    const m = /\b(?:on|for|to|of)\s+(.+)$/.exec(title);
    return norm(m ? m[1] : title);
};

export const buildRecommendations = (input: RecommendationsInput): Recommendation[] => {
    const { workforce, systems, epm } = input;
    // the staff's time, which recommendations are measured against: external roles' time isn't the organisation's work
    const peopleHours = Math.max(1, workforce?.totals.hours || 0, systems?.totals.staffHours || 0);
    // for what to act on now, how serious it is: how far over capacity (1 is twice its capacity), and for the
    // scorecard's gates, their own priority
    const candidates: (Omit<Recommendation, 'priority' | 'key' | 'why'> & { impact: number; match: string; utilisation?: number; overRatio?: number; epmPriority?: number; externalHours?: number })[] = [];

    // workforce: roles short of capacity (act now), and the data gaps it points out
    workforce?.findings.forEach(f => {
        const role = f.roleKey ? workforce.roles.find(r => r.key === f.roleKey) : undefined;
        if (f.severity === 'critical' && role) {
            // all its work counts against its capacity, its work in other models too
            const over = Math.max(0, role.hours + (role.otherHours || 0) - (role.capacity || 0));
            candidates.push({
                lever: 'capacity', urgent: true, title: `Relieve the load on ${role.name}`, match: `capacity|${norm(role.name)}`,
                detail: `Add people to the role, move some of its work to roles with spare capacity, or automate its repetitive steps.`,
                evidence: [f.detail], sources: ['workforce'], hours: over, cost: role.cost, models: role.models,
                subjects: [role.name], lens: 'people', focus: role.key, impact: over / peopleHours,
                utilisation: role.utilisation ?? undefined, overRatio: role.capacity ? over / role.capacity : 1,
            });
        } else if (f.severity === 'critical' && f.personKey) {
            const person = workforce.people.find(p => p.key === f.personKey);
            if (!person) return;
            candidates.push({
                lever: 'capacity', urgent: true, title: `Relieve the load on ${person.name}`, match: `capacity|${norm(person.name)}`,
                detail: "Reduce the share of time the person gives to one of their roles, or move some of that role's work elsewhere.",
                evidence: [f.detail], sources: ['workforce'], hours: Math.max(0, person.hours - person.capacity), cost: person.cost, models: [],
                subjects: [person.name, ...person.roles.map(r => r.name)], lens: 'people', focus: person.roles[0]?.key, impact: Math.max(0, person.hours - person.capacity) / peopleHours,
                utilisation: person.utilisation ?? undefined, overRatio: person.capacity ? Math.max(0, person.hours - person.capacity) / person.capacity : 1,
            });
        } else if (f.severity === 'warning' && role) {
            candidates.push({
                lever: role.status === 'under' ? 'value' : 'protect', urgent: false, title: f.title, match: `${role.status === 'under' ? 'value' : 'protect'}|${norm(role.name)}`,
                detail: role.status === 'under' ? 'Give the role more of the work, or review whether it needs its capacity.' : 'Spread the work or the knowledge so the role does not depend on one person.',
                evidence: [f.detail], sources: ['workforce'], hours: role.hours, cost: role.cost, models: role.models, subjects: [role.name], lens: 'people', focus: role.key,
                impact: role.hours / peopleHours,
            });
        } else if (f.severity === 'info') {
            candidates.push({
                lever: 'data', urgent: false, title: f.title, match: `data|${norm(f.title)}`, detail: f.detail, evidence: [], sources: ['workforce'], hours: 0, cost: 0, models: [], subjects: [],
                lens: 'people', impact: f.weight / peopleHours,
            });
        }
    });

    // systems: integrate, digitise and automate, and what the report warns about
    systems?.opportunities.forEach(o => {
        const subject = /"([^"]+)"/.exec(o.title)?.[1] || o.title.replace(/^Connect /, '');
        if (o.kind === 'service') {
            // external roles' time: no staff time to save, so it's sized by their time, and ranked below the savings
            candidates.push({
                lever: 'service', urgent: false, title: o.title, match: `service|${norm(subject)}`, detail: o.detail,
                evidence: [`${Math.round(o.externalHours || 0).toLocaleString('en')} hours a year of external roles' time. It saves no staff time.`],
                sources: ['systems'], hours: 0, cost: 0, models: o.models, subjects: [subject], lens: 'systems', focus: o.actionKeys[0],
                impact: (o.externalHours || 0) / peopleHours, externalHours: o.externalHours || 0,
            });
            return;
        }
        candidates.push({
            lever: o.kind, urgent: false, title: o.title, match: `${o.kind}|${norm(subject)}`, detail: o.detail, evidence: [`Up to ${Math.round(o.hours).toLocaleString('en')} hours a year of staff time in the steps involved.`],
            sources: ['systems'], hours: o.hours, cost: o.cost, models: o.models, subjects: [subject], lens: 'systems', focus: o.actionKeys[0], impact: o.hours / peopleHours,
        });
    });
    systems?.findings.filter(f => f.severity !== 'critical').forEach(f => {
        // the re-keying and off-system findings are covered by the opportunities above; the rest stand on their own
        if (/re-key|outside any system/.test(f.title)) return;
        candidates.push({
            lever: /depends on/.test(f.title) ? 'protect' : 'data', urgent: false, title: f.title, match: `${/depends on/.test(f.title) ? 'protect' : 'data'}|${norm(f.title)}`,
            detail: f.detail, evidence: [], sources: ['systems'], hours: 0, cost: f.weight, models: [], subjects: [], lens: 'systems', impact: f.weight > 1000 ? 0.05 : 0.02,
        });
    });

    // the EPM scorecard's decisions: its priority runs 0 to 100 with the strongest at 100, so they are put on the
    // same footing as the strongest of the other candidates
    const otherStrongest = Math.max(0.05, ...candidates.filter(c => c.lever !== 'data').map(c => c.impact));
    epm?.decisions.forEach(d => {
        const lever: RecommendationLever = /capacity overload/i.test(d.title) ? 'capacity' : EPM_LEVERS[d.lever] || 'value';
        const subjects = d.nodes.map(n => n.name);
        candidates.push({
            lever, urgent: d.gate, title: d.title, match: `${lever}|${titleKey(d.title)}`, detail: d.rationale, evidence: [d.signal, ...d.impacts.map(i => `${i.dimension}: ${i.movement}`)],
            sources: ['epm'], hours: 0, cost: 0, models: [], subjects, lens: 'actions', effort: d.effort, impact: otherStrongest * d.priority / 100,
            epmPriority: d.priority,
        });
    });

    // merge what says the same thing: the same lever about the same subject
    const merged = new Map<string, typeof candidates[number]>();
    candidates.forEach(c => {
        const existing = merged.get(c.match) || [...merged.values()].find(m => m.lever === c.lever && m.subjects.some(s => c.subjects.map(norm).includes(norm(s))));
        if (!existing) { merged.set(c.match, { ...c }); return; }
        existing.evidence = [...existing.evidence, ...c.evidence.filter(e => !existing.evidence.includes(e))];
        existing.sources = [...new Set([...existing.sources, ...c.sources])];
        existing.models = [...new Set([...existing.models, ...c.models])];
        existing.subjects = [...new Set([...existing.subjects, ...c.subjects])];
        existing.hours = Math.max(existing.hours, c.hours);
        existing.cost = Math.max(existing.cost, c.cost);
        existing.urgent = existing.urgent || c.urgent;
        existing.impact = Math.max(existing.impact, c.impact) + 0.1 * Math.min(existing.impact, c.impact);
        existing.effort = existing.effort || c.effort;
        existing.utilisation = Math.max(existing.utilisation ?? 0, c.utilisation ?? 0) || undefined;
        existing.overRatio = existing.overRatio === undefined && c.overRatio === undefined ? undefined : Math.max(existing.overRatio ?? 0, c.overRatio ?? 0);
        existing.epmPriority = Math.max(existing.epmPriority ?? 0, c.epmPriority ?? 0) || undefined;
        if (!existing.focus) existing.focus = c.focus;
    });

    // what to act on now scores 70 to 100, the most serious 100: how far over capacity it is counts most, then what it
    // costs (a scorecard gate with neither counts by its own priority). Everything else scores 1 to 69 by its share of
    // the staff's time against the largest; data gaps never above 20, as they are not changes, even one the scorecard
    // says to fill first (it's listed with the data gaps). Better service for external roles comes after all of those,
    // as it saves no staff time. So the number and the order agree, and nothing reads as 0
    const list = [...merged.values()];
    type Merged = typeof list[number];
    const urgent = list.filter(c => c.urgent && c.lever !== 'data');
    const maxOver = Math.max(0.0001, ...urgent.map(c => c.overRatio ?? 0));
    const maxCost = Math.max(1, ...urgent.map(c => c.cost));
    const severity = (c: Merged) => c.overRatio !== undefined || c.cost > 0
        ? 0.7 * (c.overRatio ?? 0) / maxOver + 0.3 * c.cost / maxCost
        : (c.epmPriority ?? 50) / 100;
    const maxSeverity = Math.max(0.0001, ...urgent.map(severity));
    const strongest = Math.max(0.0001, ...list.filter(c => !c.urgent && !NOT_SAVINGS.includes(c.lever)).map(c => c.impact * LEVER_WEIGHT[c.lever]));

    const count = (v: number) => Math.round(v).toLocaleString('en');
    const share = (hours: number) => {
        const pct = 100 * hours / peopleHours;
        return pct < 1 ? 'under 1%' : `${Math.round(pct)}%`;
    };
    const why = (c: Merged, priority: number): string => {
        if (c.urgent && c.lever === 'capacity' && c.overRatio !== undefined) {
            const load = c.utilisation ? `${Math.round(c.utilisation * 100)}% of capacity, ` : '';
            const cost = c.cost ? `; it costs about ${count(c.cost)} a year` : '';
            return `Priority ${priority}: act now. ${load}${count(c.hours)} ${Math.round(c.hours) === 1 ? 'hour' : 'hours'} a year more work than it can do${cost}.`;
        }
        if (c.lever === 'data') return c.urgent
            ? `Priority ${priority}: a gap in the data, which the EPM scorecard says to fill first. Filling it makes the figures more reliable; it isn't a change in itself.`
            : `Priority ${priority}: a gap in the data. Filling it makes the figures more reliable; it isn't a change in itself.`;
        if (c.urgent) return `Priority ${priority}: act now. The EPM scorecard puts it first: nothing else moves until it is done.`;
        if (c.lever === 'service') return `Priority ${priority}: better service for the external roles who do it, ${count(c.externalHours || 0)} hours a year of their time. It saves no staff time, so it ranks below the changes that do.`;
        if (c.hours > 0) return `Priority ${priority}: ${count(c.hours)} hours a year of staff time, ${share(c.hours)} of the staff time looked at.`;
        if (c.epmPriority !== undefined) return `Priority ${priority}: the EPM scorecard rates it ${c.epmPriority} of 100.`;
        return `Priority ${priority}: its share of the work looked at, against the largest.`;
    };

    const scaledOf = (c: Merged) => Math.max(1, Math.round(69 * c.impact * LEVER_WEIGHT[c.lever] / strongest));
    const scoreOf = (c: Merged) => c.lever === 'data' ? Math.min(20, scaledOf(c))
        : c.urgent ? 70 + Math.round(30 * severity(c) / maxSeverity)
        : Math.min(69, scaledOf(c));
    // better service for external roles: by their time against the largest such, up to 30, and always below the least of
    // the changes that save staff time, so the number and the order agree
    const savings = list.filter(c => !c.urgent && !NOT_SAVINGS.includes(c.lever)).map(scoreOf);
    const serviceCeiling = Math.max(1, Math.min(30, savings.length ? Math.min(...savings) - 1 : 30));
    const largestService = Math.max(0.0001, ...list.filter(c => c.lever === 'service').map(c => c.impact));
    const priorityOf = (c: Merged) => c.lever === 'service' ? Math.max(1, Math.round(serviceCeiling * c.impact / largestService)) : scoreOf(c);
    const isService = (r: { lever: RecommendationLever }) => r.lever === 'service';

    return list.map((c, i) => {
        const { impact, match, utilisation, overRatio, epmPriority, externalHours, ...rest } = c;
        const priority = priorityOf(c);
        return { ...rest, key: `${c.lever}:${match}:${i}`, priority, why: why(c, priority) };
    }).sort((a, b) => Number(b.urgent) - Number(a.urgent) || Number(isService(a)) - Number(isService(b)) || b.priority - a.priority);
};

// a short list to start with: no more than a few to act on now, so the largest of the rest are seen too (the rest come
// with "show all"). With too few others to fill it, more of those to act on now
export const previewRecommendations = (list: Recommendation[], size: number, maxUrgent: number): Recommendation[] => {
    const urgent = list.filter(r => r.urgent);
    const others = list.filter(r => !r.urgent);
    const firstUrgent = urgent.slice(0, Math.min(maxUrgent, size));
    const shownOthers = others.slice(0, size - firstUrgent.length);
    const moreUrgent = urgent.slice(firstUrgent.length, firstUrgent.length + Math.max(0, size - firstUrgent.length - shownOthers.length));
    return [...firstUrgent, ...moreUrgent, ...shownOthers];
};
